import {
  createLightTheme,
  teamsLightTheme,
  webLightTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';
import {
  ACTIVE_THEME,
  APP_THEMES,
  TENANT_THEME_KEY,
  THEME_OPTIONS,
  buildMokoTheme as buildTokenTheme,
  themeConfigFromOtherInfo,
  type BaseFluentTheme,
  type MokoThemeConfig,
} from '@moko/themes';

export {
  ACTIVE_THEME,
  APP_THEMES,
  TENANT_THEME_KEY,
  THEME_OPTIONS,
  themeConfigFromOtherInfo,
  type MokoThemeConfig,
};

const fluentBases: Record<BaseFluentTheme, Theme> = {
  web: webLightTheme,
  teams: teamsLightTheme,
};

export const buildMokoTheme = (config: MokoThemeConfig): Theme => {
  const base = fluentBases[config.baseFluentTheme ?? 'web'];
  const tokens = buildTokenTheme(config);
  const brandTheme = config.brand
    ? createLightTheme(config.brand as BrandVariants)
    : undefined;

  return {
    ...base,
    ...brandTheme,
    ...(config.font?.cssStack ? { fontFamilyBase: config.font.cssStack } : {}),
    colorNeutralBackground1: tokens.colors.backgroundPrimary,
  };
};

type GlobImporter = () => Promise<unknown>;

const fontModules = import.meta.glob([
  '../../node_modules/@fontsource-variable/*/index.css',
  '../../node_modules/@fontsource/*/[0-9]00.css',
]) as Record<string, GlobImporter>;

const resolveFontLoader = (
  specifier: string
): (() => Promise<unknown>) | undefined => {
  const suffix = specifier.replace(/^@fontsource/, '');
  const match = Object.keys(fontModules).find((path) =>
    path.includes(`@fontsource${suffix}`)
  );
  return match ? fontModules[match] : undefined;
};

export const loadThemeFonts = (fontImports?: string[]): Promise<unknown> =>
  Promise.all(
    (fontImports ?? []).map((specifier) => resolveFontLoader(specifier)?.())
  );

const STYLE_ID = 'moko-theme-css';

export const applyThemeCss = (config: MokoThemeConfig, theme: Theme): void => {
  if (typeof document === 'undefined') return;

  const fontFamily = config.font?.cssStack;

  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }

  const fontRule = fontFamily
    ? `
    :root, body, [class*='fui-FluentProvider'] {
      font-family: ${fontFamily};
    }
    button, input, select, textarea {
      font-family: inherit;
    }
  `
    : '';

  style.textContent = `
    :root {
      --moko-brand: ${theme.colorBrandForeground1};
      --moko-brand-strong: ${theme.colorBrandBackground};
      --moko-brand-light: ${theme.colorBrandBackground2};
      --moko-background: ${theme.colorNeutralBackground1};
    }
    html, body {
      background-color: ${theme.colorNeutralBackground1};
    }
    ${fontRule}
  `;
};
